<template>
  <CDialog
    v-bind="{ show }"
    @close="closeModal"
    :title="$t('application_for_buy')"
    custom-class="max-w-[600px]"
  >
    <div class="p-5">
      <div
        class="p-3 md:p-4 mb-5 border border-[#FEDF89] rounded-2xl bg-[#FEF0C7]"
      >
        <h4 class="text-[#B54708] mb-1 text-base font-semibold leading-normal">
          {{ $t("warning_application_for_replenishment_title") }}
        </h4>
        <p class="text-xs font-normal leading-130 text-[#B54708] space-y-1">
          <span
            class="inline-block"
            v-html="$t('warning_application_for_replenishment_subtitle')"
          />
          <br />
          <span
            class="inline-block"
            v-html="$t('warning_application_for_replenishment_subtitle_info_1')"
          />
          <br />
          <span
            class="inline-block"
            v-html="$t('warning_application_for_replenishment_subtitle_info_2')"
          />
          <br />
          <span
            class="inline-block"
            v-html="
              $t('warning_application_for_replenishment_subtitle_info_warning')
            "
          />
        </p>
      </div>
      <form @submit.prevent="onUpdate">
        <div class="mb-6" v-if="categories?.length">
          <FRadioGroup
            v-model="form.values.category"
            :items="categories"
            label-key="title"
            value-key="title"
            without-selected
            item-class="!w-auto"
            wrapper-class="flex flex-wrap gap-2"
          />
        </div>
        <div
          class="flex items-start sm:items-end flex-col sm:flex-row gap-4 mb-4"
        >
          <FGroup :label="$t('sum_of_fill')" class="sm:max-w-[200px]">
            <FInput
              v-maska="moneyMask"
              v-model="form.values.amount"
              :error="form.$v.value.amount?.$error"
              :maxlength="18"
              placeholder="100 000"
            />
          </FGroup>
          <div class="flex flex-col gap-1">
            <div class="flex-y-center gap-2">
              <span class="text-sm">{{ $t("amount_to_be_transfered") }}:</span>
              <span>
                {{ amountWithCommission }}
                <span class="text-xs">{{ form.values.category }}</span>
              </span>
            </div>
            <div class="flex-y-center gap-2">
              <span class="text-sm">{{ $t("estimate_process") }}:</span>
              <span class="text-green">
                {{ commissionAmount }}
                <span class="text-xs">{{ form.values.category }}</span>
              </span>
            </div>
          </div>
        </div>
        <div class="mb-6" v-if="form.values.category">
          <FRadioGroup
            v-model="form.values.defaultAmount"
            :items="defaultAmounts"
            label-key="title"
            value-key="amount"
            without-selected
            label-class="!text-xs flex-y-center !font-bold"
            item-class="!w-auto !flex !items-center !justify-center"
            wrapper-class="grid grid-cols-2 sm:grid-cols-4 gap-2"
          />
        </div>
        <FCheckbox
          class="my-3"
          :label="$t('terms_&_conditions')"
          v-model="termsInfo"
        >
          <template #label>
            <div class="flex-y-center gap-1">
              <span>{{ $t("agree_terms_condition") }}</span>
              <button @click.stop.prevent="showInfoTerms" class="text-green">
                {{ $t("click_here") }}
              </button>
            </div>
          </template>
        </FCheckbox>
        <CButton
          :text="$t('add')"
          class="w-full mt-6"
          size="sm"
          :disabled="form.$v.value.$invalid || !termsInfo"
          :loading="isLoading"
          type="submit"
        />
      </form>
    </div>
  </CDialog>
</template>

<script setup lang="ts">
import CDialog from "@/components/Common/Dialog/CDialog.vue";
import FGroup from "@/components/Form/FGroup.vue";
import FInput from "@/components/Form/Input/FInput.vue";
import { moneyMask } from "@/utils";
import CButton from "@/components/Common/CButton.vue";
import { useForm } from "@/composables/useForm";
import { required } from "@vuelidate/validators";
import { computed, ref, watch } from "vue";

import FRadioGroup from "@/components/Form/Radio/FRadioGroup.vue";
import FCheckbox from "@/components/Form/FCheckbox.vue";

interface Props {
  show: boolean;
  categories: {
    id: number;
    address: string;
    icon: string;
    qr_code: string;
    title: string;
  }[];
}
interface Emits {
  (e: "close"): void;
  (e: "showInfoTerms"): void;
  (
    e: "handleTransferAmount",
    payload: {
      id: number;
      address: string;
      icon: string;
      qr_code: string;
      title: string;
      amount: number;
      amountWithCommission: number;
    },
  ): void;
}
const emit = defineEmits<Emits>();

const commission = ref(0.02);
const props = defineProps<Props>();
const isLoading = ref(false);
const termsInfo = ref(false);

const form = useForm(
  {
    amount: "",
    category: "",
    defaultAmount: "",
  },
  {
    amount: { required },
    category: { required },
  },
);

const defaultAmounts = computed(() => [
  {
    amount: "100",
    title: "100 USDT",
  },
  {
    amount: "200",
    title: "200 USDT",
  },
  {
    amount: "500",
    title: "500 USDT",
  },
  {
    amount: "1000",
    title: "1000 USDT",
  },
]);
const amountWithCommission = computed(
  () =>
    Number(form.values.amount?.replace(/\s/g, "")) +
    Number(form.values.amount?.replace(/\s/g, "")) * commission.value,
);
const commissionAmount = computed(
  () => Number(form.values.amount?.replace(/\s/g, "")) * commission.value,
);
const activeCategory = computed(() =>
  props?.categories?.find((el) => el?.title === form.values.category),
);

const showInfoTerms = () => {
  emit("showInfoTerms");
};

const onUpdate = () => {
  form.$v.value.$touch();
  if (!form.$v.value.$invalid) {
    emit("handleTransferAmount", {
      ...activeCategory.value,
      amountWithCommission: amountWithCommission.value,
      amount: Number(form.values.amount?.replace(/\s/g, "")),
    });
    closeModal();
  }
};

const closeModal = () => {
  form.values.amount = "";
  form.values.category = "";
  form.values.defaultAmount = "";
  form.$v.value.$reset();
  emit("close");
};

watch(
  () => form.values.defaultAmount,
  (val: string) => {
    form.values.amount = val;
  },
);
watch(
  () => props?.show,
  (val: boolean) => {
    if (val && props?.categories?.length)
      form.values.category = props?.categories?.find((el) => el)?.title;
  },
  { deep: true, immediate: true },
);
</script>

<style scoped>
.qr-code {
  border-radius: 16px;
  border: 2px solid rgba(22, 204, 83, 0.5);
  background: linear-gradient(180deg, #16cc53 -230.8%, #080a15 110.94%);
}

canvas {
  display: inline-block;
  width: 82px;
  height: 82px;
}
</style>
